import React from 'react';
import {
  Box,
  Typography,
  CircularProgress, Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {Accordion, AccordionTrigger, AccordionContent, AccordionItem} from '@/components/ui/accordion';
import ShiftItem, { ShiftData } from './ShiftItem';
import {formatCurrency, formatDateTime} from "@/utils/formatters.ts";
import {useIsMobile} from "@/hooks/use-mobile.tsx";

interface ShiftsListProps {
  shifts: ShiftData[];
  loading: boolean;
  error: string | null;
}

const ShiftsList: React.FC<ShiftsListProps> = ({ shifts, loading, error }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" my={2}>
        {error}
      </Typography>
    );
  }

  if (shifts.length === 0) {
    return (
      <Typography align="center" color="text.secondary" my={2}>
        {t('assignments.noShifts')}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Accordion type="single" collapsible className="w-full">
        {shifts.map((shift) => (
          <AccordionItem key={shift.uuid} value={shift.uuid}>
            <AccordionTrigger className="hover:no-underline">
              <Box sx={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1">
                    {t('assignments.shift')} #{shift.sequenceNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(shift.startAt)} - {formatDateTime(shift.endAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={formatCurrency(shift.finalTotalAmount)}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`${shift.tripsCount} ${t('assignments.trips')}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionTrigger>
            <AccordionContent>
              <ShiftItem key={shift.uuid} shift={shift} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
};

export default ShiftsList;
